import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import {
  ChatContainer,
  ChatHeader,
  ChatTitle,
  ChatBody,
  ChatFooter,
  ToggleClosedButton,
  HeaderActions,
  InputField,
  SendButton,
  SuggestedQuestionsContainer,
  SuggestedQuestionChip,
  WelcomeMessage,
  SuggestedQuestionsLabel,
  FollowUpQuestionsContainer,
  FollowUpQuestionChip,
  FollowUpQuestionsLabel,
  SeeOtherOptionsButton,
  MessageBubble,
  LoadingIndicator,
} from './styles';
import { useAuth } from '../../context/authContext';
import api from '../../services/api';

interface ChatMessage {
  id: string;
  text: string;
  role: 'user' | 'assistant' | 'system';
}

interface SuggestedQuestion {
  id: string;
  text: string;
  question: string;
}

const storageKey = 'victor-insights:chatWidget:isOpen';

const suggestedQuestions: SuggestedQuestion[] = [
  {
    id: '1',
    text: 'Wie genau funktioniert das Cockpit?',
    question: 'Wie genau funktioniert das Cockpit?',
  },
  {
    id: '2',
    text: 'Was genau kann ich mit dem Reiter „To-Do-Liste“ machen?',
    question: 'Was genau kann ich mit dem Reiter „To-Do-Liste“ machen?',
  },
  {
    id: '3',
    text: 'Wie komme ich zu den Berichten?',
    question: 'Wie komme ich zu den Berichten?',
  },
];

const ChatWidget: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [dynamicQuestions, setDynamicQuestions] =
    useState<SuggestedQuestion[]>(suggestedQuestions);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState<boolean>(false);
  const [followUpQuestions, setFollowUpQuestions] = useState<
    SuggestedQuestion[]
  >([]);
  const [isLoadingFollowUps, setIsLoadingFollowUps] = useState<boolean>(false);
  const [showMainSuggestions, setShowMainSuggestions] =
    useState<boolean>(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const typewriterIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (typewriterIntervalRef.current) {
        clearInterval(typewriterIntervalRef.current);
        typewriterIntervalRef.current = null;
      }
    };
  }, []);

  const scrollToBottom = useCallback((): void => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const fetchSuggestedQuestions = useCallback(async (): Promise<void> => {
    if (isLoadingQuestions) return;

    setIsLoadingQuestions(true);
    try {
      const response = await api.post('/assistant/chat/followups', {
        message: 'häufige Fragen Funktionen Hilfe Überblick',
      });

      if (response.statusText === 'OK' && response.data?.questions) {
        const questions: SuggestedQuestion[] = response.data.questions.map(
          (text: string, index: number) => ({
            id: `dynamic-${index + 1}`,
            text,
            question: text.toLowerCase(),
          }),
        );
        if (questions.length > 0) {
          setDynamicQuestions(questions);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch dynamic questions, using fallback:', error);
    } finally {
      setIsLoadingQuestions(false);
    }
  }, [isLoadingQuestions]);

  const fetchFollowUpQuestions = useCallback(
    async (userQuestion: string): Promise<void> => {
      if (isLoadingFollowUps) return;

      setIsLoadingFollowUps(true);
      try {
        const response = await api.post('/assistant/chat/followups', {
          message: userQuestion,
        });

        if (response.statusText === 'OK' && response.data?.questions) {
          const questions: SuggestedQuestion[] = response.data.questions.map(
            (text: string, index: number) => ({
              id: `followup-${index + 1}`,
              text,
              question: text.toLocaleLowerCase(),
            }),
          );
          setFollowUpQuestions(questions);
        }
      } catch (error) {
        console.warn('Failed to fetch follow-up questions:', error);
        setFollowUpQuestions([]);
      } finally {
        setIsLoadingFollowUps(false);
      }
    },
    [isLoadingFollowUps],
  );

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) {
        setIsOpen(saved === 'true');
      }
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(isOpen));
    } catch (err) {
      // ignore
    }
  }, [isOpen]);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [
    messages,
    isOpen,
    followUpQuestions,
    showMainSuggestions,
    scrollToBottom,
  ]);

  useEffect(() => {
    if (
      isOpen &&
      messages.length === 0 &&
      dynamicQuestions === suggestedQuestions
    ) {
      fetchSuggestedQuestions();
    }
  }, [isOpen, messages.length, dynamicQuestions, fetchSuggestedQuestions]);

  // Scroll to bottom when follow-up questions appear
  useEffect(() => {
    if (followUpQuestions.length > 0) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [followUpQuestions.length, scrollToBottom]);

  const showMainOptions = useCallback(() => {
    setFollowUpQuestions([]);
    setShowMainSuggestions(true);
    // Small delay to ensure DOM updates before scrolling
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, [scrollToBottom]);

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (trimmed.length === 0 || isSending) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      text: trimmed,
      role: 'user',
    };

    const assistantMessageId = crypto.randomUUID();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      text: '',
      role: 'assistant',
    };

    setMessages(prev => [...prev, userMessage, assistantMessage]);
    setInputValue('');
    setIsSending(true);
    setFollowUpQuestions([]);
    setShowMainSuggestions(false);

    try {
      const response = await api.post('/assistant/chat', {
        message: trimmed,
        usergroupId: user?.selectedUsergroup?.userGroupId,
      });

      if (response.statusText !== 'OK') {
        throw new Error(`Request failed with status ${response.status}`);
      }
      const { data } = response;

      if (data) {
        // Typewriter effect - simulate typing
        const fullText = data;
        let currentText = '';
        const words = fullText.split(' ');
        let wordIndex = 0;

        if (typewriterIntervalRef.current) {
          clearInterval(typewriterIntervalRef.current);
        }
        typewriterIntervalRef.current = setInterval(() => {
          if (wordIndex < words.length) {
            currentText += (wordIndex > 0 ? ' ' : '') + words[wordIndex];
            setMessages(prev =>
              prev.map(m =>
                m.id === assistantMessageId ? { ...m, text: currentText } : m,
              ),
            );
            wordIndex += 1;
          } else {
            if (typewriterIntervalRef.current) {
              clearInterval(typewriterIntervalRef.current);
              typewriterIntervalRef.current = null;
            }
            setIsSending(false);
            // Fetch follow-up questions after response is complete
            if (trimmed) {
              fetchFollowUpQuestions(trimmed);
            }
          }
        }, 50); // Adjust speed here - 50ms per word
      } else {
        throw new Error('Error to get the response from the assistant.');
      }
    } catch (error) {
      console.error('Error', error);
      setIsSending(false); // Reset sending state on error
    }
  }, [inputValue, isSending, fetchFollowUpQuestions, user]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleSuggestedQuestionClick = useCallback(
    async (question: string) => {
      if (isSending) return;

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        text: question,
        role: 'user',
      };

      const assistantMessageId = crypto.randomUUID();
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        text: '',
        role: 'assistant',
      };

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setIsSending(true);
      setFollowUpQuestions([]);
      setShowMainSuggestions(false);

      try {
        const response = await api.post('/assistant/chat', {
          message: question,
          usergroupId: user?.selectedUsergroup?.userGroupId,
        });

        if (response.statusText !== 'OK') {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const { data } = response;

        if (data) {
          const fullText = data;
          let currentText = '';
          const words = fullText.split(' ');
          let wordIndex = 0;

          if (typewriterIntervalRef.current) {
            clearInterval(typewriterIntervalRef.current);
          }
          typewriterIntervalRef.current = setInterval(() => {
            if (wordIndex < words.length) {
              currentText += (wordIndex > 0 ? ' ' : '') + words[wordIndex];
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantMessageId ? { ...m, text: currentText } : m,
                ),
              );
              wordIndex += 1;
            } else {
              if (typewriterIntervalRef.current) {
                clearInterval(typewriterIntervalRef.current);
                typewriterIntervalRef.current = null;
              }
              setIsSending(false);
              // Fetch follow-up questions after response is complete
              if (question) {
                fetchFollowUpQuestions(question);
              }
            }
          }, 50);
        } else {
          throw new Error('Error to get the response from the assistant.');
        }
      } catch (error) {
        console.error('Error', error);
        setIsSending(false);
      }
    },
    [isSending, fetchFollowUpQuestions, user],
  );

  const minimizedButton = useMemo(
    () => (
      <ToggleClosedButton
        aria-label="Open chat"
        onClick={toggle}
        title="Open chat"
      >
        <ChatBubbleOutlineIcon />
      </ToggleClosedButton>
    ),
    [toggle],
  );

  // Don't render chat widget if user is not logged in
  if (!user) {
    return null;
  }

  if (!isOpen) {
    return minimizedButton;
  }

  return (
    <>
      <ChatContainer role="complementary" aria-label="Victor Insights Chatbot">
        <ChatHeader>
          <ChatTitle>Insights Assistant</ChatTitle>
          <HeaderActions>
            <button aria-label="Minimize" onClick={toggle}>
              <MinimizeIcon fontSize="small" />
            </button>
            <button aria-label="Close" onClick={toggle}>
              <CloseIcon fontSize="small" />
            </button>
          </HeaderActions>
        </ChatHeader>

        <ChatBody ref={bodyRef}>
          {messages.length === 0 ? (
            <div>
              <WelcomeMessage>
                Fragen Sie mich, wie Sie Funktionen und Dashboards finden oder
                wie Sie die App verwenden.
              </WelcomeMessage>
              <SuggestedQuestionsLabel>
                Versuchen Sie es mit diesen häufig gestellten Fragen:
              </SuggestedQuestionsLabel>
              <SuggestedQuestionsContainer>
                {isLoadingQuestions ? (
                  <div
                    style={{
                      padding: '12px',
                      textAlign: 'center',
                      fontSize: '12px',
                      color: '#666',
                      fontStyle: 'italic',
                    }}
                  >
                    Personalisierte Vorschläge werden geladen...
                  </div>
                ) : (
                  dynamicQuestions.map(suggestion => (
                    <SuggestedQuestionChip
                      key={suggestion.id}
                      onClick={() =>
                        handleSuggestedQuestionClick(suggestion.question)
                      }
                      disabled={isSending}
                    >
                      {suggestion.question}
                    </SuggestedQuestionChip>
                  ))
                )}
              </SuggestedQuestionsContainer>
            </div>
          ) : (
            messages.map(message => (
              <MessageBubble key={message.id} $isUser={message.role === 'user'}>
                {message.role === 'assistant' && message.text === '' ? (
                  <LoadingIndicator aria-label="Loading response">
                    <span />
                    <span />
                    <span />
                  </LoadingIndicator>
                ) : (
                  message.text
                )}
              </MessageBubble>
            ))
          )}
          {messages.length > 0 &&
            (followUpQuestions.length > 0 || isLoadingFollowUps) && (
              <FollowUpQuestionsContainer>
                <FollowUpQuestionsLabel>
                  Ähnliche Fragen, die Sie sich stellen könnten:
                </FollowUpQuestionsLabel>
                {isLoadingFollowUps ? (
                  <div
                    style={{
                      padding: '8px',
                      textAlign: 'center',
                      fontSize: '11px',
                      color: '#666',
                      fontStyle: 'italic',
                    }}
                  >
                    Verwandte Fragen finden...
                  </div>
                ) : (
                  <>
                    {followUpQuestions.map(suggestion => (
                      <FollowUpQuestionChip
                        key={suggestion.id}
                        onClick={() =>
                          handleSuggestedQuestionClick(suggestion.question)
                        }
                        disabled={isSending}
                      >
                        {suggestion.text}
                      </FollowUpQuestionChip>
                    ))}
                    <SeeOtherOptionsButton
                      onClick={showMainOptions}
                      disabled={isSending}
                    >
                      Weitere Optionen finden Sie hier
                    </SeeOtherOptionsButton>
                  </>
                )}
              </FollowUpQuestionsContainer>
            )}
          {messages.length > 0 && showMainSuggestions && (
            <FollowUpQuestionsContainer>
              <FollowUpQuestionsLabel>
                Here are some options you might ask:
              </FollowUpQuestionsLabel>
              <SuggestedQuestionsContainer>
                {isLoadingQuestions ? (
                  <div
                    style={{
                      padding: '12px',
                      textAlign: 'center',
                      fontSize: '12px',
                      color: '#666',
                      fontStyle: 'italic',
                    }}
                  >
                    Personalisierte Vorschläge werden geladen...
                  </div>
                ) : (
                  dynamicQuestions.map(suggestion => (
                    <SuggestedQuestionChip
                      key={suggestion.id}
                      onClick={() =>
                        handleSuggestedQuestionClick(suggestion.question)
                      }
                      disabled={isSending}
                    >
                      {suggestion.text}
                    </SuggestedQuestionChip>
                  ))
                )}
              </SuggestedQuestionsContainer>
            </FollowUpQuestionsContainer>
          )}
        </ChatBody>

        <ChatFooter>
          <InputField
            placeholder="Stellen Sie Ihre Frage..."
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Chat input"
          />
          <SendButton
            onClick={handleSend}
            disabled={inputValue.trim().length === 0 || isSending}
          >
            {isSending ? 'Senden…' : 'Senden'}
          </SendButton>
        </ChatFooter>
      </ChatContainer>
    </>
  );
};

export default ChatWidget;
